import { useEffect, useState } from 'react';
import { useRouter, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  Clock,
  GraduationCap,
  Loader2,
} from 'lucide-react';

import { courseQueries, type CourseContent } from '@/entities/course';
import {
  postCreateCheckoutSession,
  saveCheckoutIntent,
  type CreateCheckoutSessionRequest,
} from '@/entities/payments';
import {
  toMetaContentId,
  trackInitiateCheckout,
} from '@/shared/analytics/meta-pixel';
import { resolveErrorMessage } from '@/shared/lib/errors';
import { useToast } from '@/shared/ui/toast-provider';
import { Button } from '@/shared/ui/button';
import { PageContainer } from '@/shared/ui/page-container';

const pageBackgroundClass = 'min-h-screen bg-slate-50 text-slate-700';

const formatDuration = (durationSecs: number) => {
  const hours = Math.floor(durationSecs / 3600);
  const minutes = Math.floor((durationSecs % 3600) / 60);
  if (hours <= 0) {
    return `${minutes} Minute${minutes !== 1 ? 's' : ''}`;
  }
  if (minutes === 0) {
    return `${hours} Hour Total`;
  }

  return `${hours} Hour${hours === 1 ? '' : 's'} ${minutes} Minute${
    minutes === 1 ? '' : 's'
  }`;
};

const formatPrice = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);

export const SelectCoursePage = () => {
  const router = useRouter();
  const toast = useToast();
  const search = useSearch({ from: '/_user/payment/select-course' });
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);

  useEffect(() => {
    if (search.status === 'success' && search.session_id) {
      router.navigate({
        to: '/payment/success',
        search: {
          session_id: search.session_id,
        },
      });
    } else if (search.status === 'cancel') {
      router.navigate({ to: '/payment/cancel' });
    }
  }, [router, search.session_id, search.status]);

  const {
    data,
    isPending: isCoursesLoading,
    isError,
    error,
  } = useQuery({
    ...courseQueries.list(),
    select: (courses) => courses.filter((course) => course.is_active),
  });

  const activeCourses = data ?? [];

  const createCheckoutMutation = useMutation({
    mutationFn: (payload: CreateCheckoutSessionRequest) =>
      postCreateCheckoutSession(payload),
  });

  useEffect(() => {
    if (!isError || !error) return;
    toast({
      type: 'error',
      title: 'We could not load available packages.',
      description: resolveErrorMessage(
        error,
        'Refresh the page or try again later.',
      ),
    });
  }, [isError, error, toast]);

  const handleSelectCourse = async (course: CourseContent) => {
    setSelectedCourseId(course.id);
    // Persisted across the Stripe round trip; the success page falls back to
    // it when the fulfill response carries no purchase values.
    saveCheckoutIntent({
      course_id: course.id,
      course_title: course.title,
      price: course.price,
      currency: 'USD',
      created_at: new Date().toISOString(),
    });
    trackInitiateCheckout({
      value: course.price,
      currency: 'USD',
      content_ids: [toMetaContentId(course.id)],
      content_name: course.title,
      content_type: 'product',
      num_items: 1,
    });
    try {
      const returnUrl =
        typeof window !== 'undefined'
          ? `${window.location.origin}/payment/select-course`
          : '/payment/select-course';
      const checkoutSession = await createCheckoutMutation.mutateAsync({
        stripe_price_lookup_key: course.stripe_price_lookup_key,
        return_url: returnUrl,
      });
      if (checkoutSession?.next_url) {
        window.location.href = checkoutSession.next_url;
        return;
      }
      toast({
        type: 'error',
        title: 'Unable to start checkout',
        description:
          'We could not open the payment page. Please try again shortly.',
      });
    } catch (mutationError) {
      const errorMessage = resolveErrorMessage(
        mutationError,
        'Please try again in a few moments.',
      );

      toast({
        type: 'error',
        title: 'Payment session failed',
        description: errorMessage,
      });
      setSelectedCourseId(null);
    }
  };

  const isProcessing = createCheckoutMutation.isPending;

  return (
    <main className={pageBackgroundClass}>
      <PageContainer maxWidth="mobile">
        <div className="flex flex-col py-12">
          <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-slate-900">
            Select your Package
          </h1>
          <p className="text-sm text-slate-500">
            Choose the desired package from the list below.
          </p>
        </div>

        <div className="mt-8 space-y-4">
          {isCoursesLoading ? (
            <div className="h-48 animate-pulse rounded-xl border border-slate-200 bg-slate-100" />
          ) : activeCourses.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-6 py-10 text-center text-sm text-slate-500">
              No active packages are available at the moment. Please check back
              later or contact support for assistance.
            </div>
          ) : (
            activeCourses.map((course) => (
              <article
                key={course.id}
                className="flex flex-col gap-6 rounded-xl border border-slate-200 bg-white px-6 py-7 shadow-sm"
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center text-blue-500">
                    <GraduationCap className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">
                      {course.title}
                    </h2>
                    <p className="mt-1 text-sm leading-relaxed text-slate-500">
                      {course.description}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                  <span className="inline-flex items-center gap-2 text-slate-600">
                    <Clock className="h-4 w-4" />
                    {formatDuration(course.duration_secs)}
                  </span>
                </div>

                <div className="space-y-2 text-sm text-slate-600">
                  <h3 className="font-semibold text-slate-800">
                    Included Courses:
                  </h3>
                  <ul className="space-y-1">
                    {course.contents.slice(0, 3).map((content) => (
                      <li key={content.id} className="leading-snug">
                        - {content.title}{' '}
                        <span className="text-slate-400">
                          ({content.credit_hours}{' '}
                          {content.credit_hours === 1 ? 'Hour' : 'Hours'})
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-4  border-t border-gray-100 pt-3">
                  <span className="text-2xl font-semibold text-blue-600 ">
                    {formatPrice(course.price)}
                  </span>
                  <Button
                    onClick={() => handleSelectCourse(course)}
                    disabled={isProcessing && selectedCourseId !== course.id}
                    variant="default"
                    size="lg"
                    className="min-w-[11rem] gap-2"
                  >
                    {isProcessing && selectedCourseId === course.id ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Redirecting...
                      </>
                    ) : (
                      <>
                        Select Package
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </article>
            ))
          )}
        </div>
        </div>
      </PageContainer>
    </main>
  );
};
